import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import _ from 'lodash';
import styled from 'styled-components';
import moment from 'moment';
import { connect } from 'react-redux';
import i18n from 'i18next';
import { withTranslation } from 'react-i18next';

import {
  getFreqConversionFactor,
  getScaledEnvelope,
  getAmplitudeConversionReference,
  convertToDbScale,
  convertFromDbScale
} from 'common/utils';
import { transformSpectrumCoordinates, handleAmplitudeTypeChange, getPointShaftSpeed } from 'common/utils/spectrumTransformations';
import { nextSpectrumXUnit } from 'common/utils/spectrumDisplay';

import colors from 'common/styles/colors';
import { getShaftSpeed } from 'home/AssetHierarchy/helpers/helpers';

import { LineChart } from '../../../../../common/components/Chart/hoc/LineChart';
import InlineModal from '../../../../../common/components/Chart/components/InlineModal';
import { KEYBOARD_CODES, markersToSkip } from '../../../../../common/constants';
import { getComponentTypeShortHand } from '../../../../AssetHierarchy/constants/assetHierarchy.constants';
import Text from '../../../../../common/typography/Text/Text';
import Button from '../../../../../common/components/atoms/Button';
import Label from '../../../../../common/typography/Label/Label';
import FlexContainer from '../../../../../common/components/atoms/FlexContainer';
import NewPeakModal from '../../../../../common/components/Chart/components/NewPeakModal';

import * as graphUtils from '../../../../../common/components/Chart/utils/graphUtils';
import * as helpers from '../../../../../common/components/Chart/utils/helpers';
import { addOutlierRightClickEvent, cleanupRightClickEvent } from '../../../../../common/components/Chart/utils/rightClickUtils';
import { toFixedXIfNumber, isElementPartiallyVisible } from '../../../../../common/utils';
import { humanize } from '../../../../../common/helpers/humanize';


const FrequencyTypeContainer = styled(Label)`
  color: ${colors.greyXD};
  margin-right: 5px;
`;

const FreqContributionContainer = styled(Label)`
  margin-right: 8px;
  font-size: 10px;
`;

const FeatureInfoContainer = styled.div`
  margin: 0;
  margin-bottom: 1em;
  margin-right: 1.5em;
  ${Label} {
    display: block;
  }
  ${Text} {
    font-size: 14px;
  }
`;
const FeatureContainerButton = styled(Button)`
  margin: 0;
  margin-top: 1em;
  margin-right: 1.5em;
  font-weight: 600;
`;

const FrequencyConversionButton = styled(Button)`
  position: relative;
  left: ${props => props.left}px;
  top: 18px;
  font-size: 13px;
  font-weight: 600;
  &:disabled {
    color: ${colors.green};
    cursor: not-allowed;
  }
`;

const ComputeContainer = styled(FlexContainer)`
  border: 0.1px ${colors.white} solid;
  border-radius: 4px;
  width: auto;
  height: 20px;
  box-shadow: 0 0 16px 0 rgba(0,0,0,0.16);
  position: absolute;
  top: 5px;
  right: 30px;
`;

const X_UNITS_MIN_PIXEL_DIFFERENCE = 30;

class SpectrumChart extends Component {
  constructor(props) {
    super(props);
    this.drawCursor = this.drawCursor.bind(this);
    this.drawCursors = this.drawCursors.bind(this);
    this.drawCursorInfotext = this.drawCursorInfotext.bind(this);
    this.drawFeatures = this.drawFeatures.bind(this);
    this.drawFeature = this.drawFeature.bind(this);
    this.highlightSelectedFeature = this.highlightSelectedFeature.bind(this);
    this.closeFeatureContainer = this.closeFeatureContainer.bind(this);
    this.addDoubleClickEventListener = this.addDoubleClickEventListener.bind(this);
    this.addRightMouseClickEvent = this.addRightMouseClickEvent.bind(this);
    this.toggleXaxisUnits = this.toggleXaxisUnits.bind(this);
    this.toggleYaxisUnits = this.toggleYaxisUnits.bind(this);
    this.storeBrushSelection = this.storeBrushSelection.bind(this);

    // Prefer parent yUnit (includes spectrum dB/linear choice); initialYunit is backend amp_units only.
    let initialYunit = props.yUnit || props.initialYunit || '';
    if (props.showOverlay && props.selectedPoints && props.selectedPoints.length > 0) {
      // Find first point with a defined yUnit
      const pointWithYUnit = props.selectedPoints.find(p => p.yUnit);
      if (pointWithYUnit && pointWithYUnit.yUnit) {
        initialYunit = pointWithYUnit.yUnit;
      }
    }

    const desiredXScale = props.currentUser.spectrum_x_scale;
    this.state = {
      dataPos: {
        y: false,
        x: false
      },
      showFeatureContainer: null,
      harmonicNumber: 1,
      dataToDisplay: props.data || [],
      xUnit: props.xUnit || 'Hz',
      yUnit: initialYunit,
      envelopeXUnit: props.envelopeXUnit || props.xUnit || 'Hz',
      features: props.features || { items: [] },
      storeBrushSelection: [],
      dataPoint: {},
      cursorType: null,
      spectralEnvelope: props.spectralEnvelope,
      points: [],
      showNewPeakModal: false,
      frequencyContribution: {},
      allowYUnitToggle: this.allowYUnitToggle(),
      spectrumXScale: props.spectrumXScale ? props.spectrumXScale : desiredXScale,
      originalData: props.originalData,
      dataStartIndex: 0,
      dataEndIndex: 0,
      selectedFeature: null,
      loadingFeatures: false,
      hoveredLegendItem: null,
      activeLegendItem: null,
      referenceShaftSpeed: null
    };
  }

  componentDidMount() {
    window.addEventListener('keydown', this.handleKeyStrokes);

    // Initialize coordinate system properly for orders mode.
    // In controlled-units mode the parent owns unit state (including preference
    // initialization), so the chart never initiates unit changes by itself.
    if (!this.props.controlledUnits && !_.isEmpty(this.props.data)) {
      const shaft_speed = this.getShaftSpeed();
      // Initialize orders mode with proper shaft speed reference
      if (this.state.spectrumXScale === 'orders' && shaft_speed) {
        // Store reference shaft speed in state to maintain consistency
        this.setState({ referenceShaftSpeed: shaft_speed }, () => {
          // Initialize orders mode after shaft speed is set
          this.toggleXaxisUnits('Orders');
        });
      }
    }

    if (this.props.tag_type === 'vibration') this.calculateFrequenciesContribution();
    // Controlled-units mode: envelope arrives pre-converted from the parent derivation.
    if (!this.props.controlledUnits) {
      this.scaleEnvelopeIfRequired();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeyStrokes);
    // Clean up right-click event listener to prevent memory leaks
    cleanupRightClickEvent(this.ctx);
  }

  componentDidUpdate(prevProps, prevState) {
    if (!_.isEqual(this.props.originalData, prevProps.originalData)) {
      this.setState({ originalData: this.props.originalData });
    }

    if (this.props.features && !this.state.features && !this.state.loadingFeatures) {
      this.setState({ features: this.props.features, loadingFeatures: false });
    }

    if (this.props.spectralEnvelope && !_.isEqual(this.props.spectralEnvelope, prevProps.spectralEnvelope)) {
      this.setState({
        spectralEnvelope: this.props.spectralEnvelope,
        envelopeXUnit: this.props.envelopeXUnit
      }, () => {
        // Controlled-units mode: the envelope arrives already converted to the
        // display unit alongside the data, so never initiate a unit change here.
        // An envelope with no x_units must not toggle: toggleXaxisUnits(undefined) cycles to the next unit.
        if (!this.props.controlledUnits && this.props.envelopeXUnit != null &&
          this.props.spectralEnvelope && this.state.xUnit !== this.props.envelopeXUnit) {
          this.toggleXaxisUnits(this.props.envelopeXUnit);
        } else if (!this.props.showOverlay) {
          this.drawEnvelope();
        }
      });
    }

    // REFACTORED: Centralized feature redraw logic with debouncing
    let shouldRedrawFeatures = false;
    let shouldRedrawData = false;
    let shouldUpdateShaftSpeed = false;

    // Check for changes that would affect shaft speed
    if (!_.isEqual(this.props.shaftSpeed, prevProps.shaftSpeed) ||
      !_.isEqual(this.props.componentNode, prevProps.componentNode) ||
      !_.isEqual(this.props.measurementTimestamp, prevProps.measurementTimestamp)) {
      shouldUpdateShaftSpeed = true;
    }

    if (this.props.showOverlay !== prevProps.showOverlay) {
      shouldRedrawFeatures = true;

      // Handle envelope alarm visibility when overlay state changes
      if (this.props.spectralEnvelope) {
        if (this.props.showOverlay) {
          // Hide envelope alarm when overlay is enabled
          d3.selectAll(`.${this.props.chartName}.step-graph.dataline`).remove();
          d3.selectAll(`.${this.props.chartName}.step-graph.circle`).remove();
        } else {
          // Show envelope alarm when overlay is disabled
          this.drawEnvelope();
        }
      }

      // When exiting overlay mode, reset state to clear any lingering overlay-related settings
      if (!this.props.showOverlay) {
        this.setState({
          points: [],
          // In controlled-units mode the parent owns the amplitude unit and it
          // flows down via props.yUnit; never reset it locally.
          ...(this.props.controlledUnits ? {} : { yUnit: this.props.initialYunit || '' }),
          hoveredLegendItem: null,
          activeLegendItem: null,
          features: this.getFeatures(),
        });
      } else {
        this.setState({ points: [] });
      }
    }

    // Watch for Y-axis unit changes specifically.
    // Skipped in controlled-units mode: overlay point data is derived (already
    // converted) by the parent, so converting it again here would corrupt it.
    if (!this.props.controlledUnits && prevState.yUnit !== this.state.yUnit && this.props.showOverlay) {
      // Y-axis unit has changed in overlay mode, update all points
      const { selectedPoints } = this.props;
      if (selectedPoints && selectedPoints.length > 0 && this.props.updateSelectedPoints) {
        const reference = getAmplitudeConversionReference(prevState.yUnit, this.state.yUnit);
        if (reference) {
          const updatedPoints = _.cloneDeep(selectedPoints);
          let needsUpdate = false;

          updatedPoints.forEach((point, index) => {
            if (point.spectrum_data && (!point.yUnit || point.yUnit !== this.state.yUnit)) {
              needsUpdate = true;

              // Enhanced Y-axis conversion for amplitude type changes
              // This ensures proper coordinate system stability when switching between
              // velocity and acceleration data in overlay mode

              // Check if this is an amplitude type change (not just unit change)
              const isAmplitudeTypeChange = this.isAmplitudeTypeChange(point.yUnit || prevState.yUnit, this.state.yUnit);

              if (isAmplitudeTypeChange) {
                // Use proper amplitude type conversion for coordinate system stability
                const currentAmplitudeType = this.props.amp_type;

                if (currentAmplitudeType) {
                  const coordinateParams = {
                    currentXUnit: this.state.xUnit,
                    currentYUnit: this.state.yUnit,
                    shaftSpeed: this.state.referenceShaftSpeed || this.getShaftSpeed(),
                    unitSystem: this.props.unitSystem || 1
                  };

                  // ENHANCED: Preserve overlay metadata during transformation
                  const preservedMetadata = {
                    color: point.color,
                    overlayColor: point.overlayColor,
                    description: point.description,
                    timestamp: point.timestamp,
                    pointIndex: point.pointIndex || index,
                    originalIndex: point.originalIndex || index
                  };

                  const transformedPoints = handleAmplitudeTypeChange([point], currentAmplitudeType, coordinateParams);
                  if (transformedPoints.length > 0) {
                    // Merge preserved metadata with transformed point
                    updatedPoints[index] = {
                      ...transformedPoints[0],
                      ...preservedMetadata,
                      // Ensure transformation metadata is updated
                      lastRefreshAmpType: currentAmplitudeType,
                      amplitudeTransformHistory: (point.amplitudeTransformHistory || []).concat([{
                        timestamp: Date.now(),
                        fromType: point.lastRefreshAmpType || 'unknown',
                        toType: currentAmplitudeType,
                        fromYUnit: point.yUnit || prevState.yUnit,
                        toYUnit: this.state.yUnit,
                        shaftSpeed: this.state.referenceShaftSpeed || this.getShaftSpeed()
                      }])
                    };
                  }
                }
              } else {
                // Standard unit conversion (e.g., dB conversion)
                if (this.state.yUnit.toLowerCase() === 'db' &&
                  (!point.yUnit || point.yUnit.toLowerCase() !== 'db')) {
                  // Convert to dB
                  updatedPoints[index].spectrum_data = point.spectrum_data.map(d => ({
                    x: d.x,
                    y: convertToDbScale(d.y, reference)
                  }));
                } else if (this.state.yUnit.toLowerCase() !== 'db' &&
                  point.yUnit && point.yUnit.toLowerCase() === 'db') {
                  // Convert from dB
                  updatedPoints[index].spectrum_data = point.spectrum_data.map(d => ({
                    x: d.x,
                    y: convertFromDbScale(d.y, reference)
                  }));
                } else {
                  // Handle other non-dB unit conversions
                  const conversionFactor = reference.factor || 1;
                  if (conversionFactor !== 1) {
                    updatedPoints[index].spectrum_data = point.spectrum_data.map(d => ({
                      x: d.x,
                      y: d.y * conversionFactor
                    }));
                  }
                }

                // Update the unit and ensure coordinate system metadata is preserved
                updatedPoints[index].yUnit = this.state.yUnit;
              }

              // Preserve coordinate system stability during amplitude type changes
              // Maintain reference shaft speed for consistent Orders mode calculations
              if (this.state.referenceShaftSpeed && !updatedPoints[index].referenceShaftSpeed) {
                updatedPoints[index].referenceShaftSpeed = this.state.referenceShaftSpeed;
              }
            }
          });

          if (needsUpdate) {
            this.props.updateSelectedPoints(updatedPoints);
          }
        }
      }
    }

    if (!_.isEqual(this.props.selectedPoints, prevProps.selectedPoints) ||
      !_.isEqual(prevProps.features, this.props.features) ||
      this.props.feature !== prevProps.feature) {
      shouldRedrawFeatures = true;
    }

    // Handle legend item changes
    if (prevProps.hoveredLegendItem !== this.props.hoveredLegendItem ||
      prevProps.activeLegendItem !== this.props.activeLegendItem) {
      shouldRedrawData = true;
      shouldRedrawFeatures = true;
    }

    // Handle shaft speed updates properly
    if (shouldUpdateShaftSpeed) {
      const newShaftSpeed = this.getShaftSpeed();
      if (newShaftSpeed !== this.state.referenceShaftSpeed) {
        this.setState({ referenceShaftSpeed: newShaftSpeed }, () => {
          // Force feature redraw with new shaft speed
          this.setState({ features: this.getFeatures() }, () => {
            this.drawFeatures();
          });
        });
      }
    }

    // REFACTORED: Execute redraws only when necessary and batch them
    if (this.ctx) {
      if (shouldRedrawData) {
        this.ctx.drawDataLines();
      }
      if (shouldRedrawFeatures) {
        // Debounce feature redraws to prevent excessive calls
        this.debouncedDrawFeatures();
      }
    }

    if (this.props.resetChart) {
      this.props.resetChart();
    }

    if (!_.isEqual(this.props.tag_type, prevProps.tag_type) ||
      !_.isEqual(this.props.updateEnvelopeData, prevProps.updateEnvelopeData)) {
      this.setState({
        allowYUnitToggle: this.allowYUnitToggle()
      });
    }

    if (!this.props.controlledUnits && _.isEmpty(prevProps.data) && !_.isEmpty(this.props.data)) {
      const shaft_speed = this.getShaftSpeed();
      if (this.state.spectrumXScale === 'orders' && shaft_speed) this.toggleXaxisUnits('Orders');
    }

    // Controlled-units mode: data, features and units are derived together by
    // the parent and flow down as props (the chart HOC redraws from props.data);
    // only mirror features into local state for the draw helpers.
    if (this.props.controlledUnits && this.props.features !== prevProps.features) {
      this.setState({ features: this.getFeatures() });
    }

    if (!_.isEqual(this.props.xUnit, prevProps.xUnit)) {
      const hasActiveSpectrumCursors =
        this.state.points &&
        this.state.points.length > 0 &&
        (this.state.cursorType === 'sideband' || this.state.cursorType === 'harmonic');

      if (hasActiveSpectrumCursors) {
        // Parent already transformed the series; sync state first so toggleXaxisUnits only remaps stale cursors.
        this.setState({ xUnit: this.props.xUnit }, () => this.toggleXaxisUnits(this.props.xUnit));
      } else {
        this.setState({ xUnit: this.props.xUnit });
      }
    }

    if (this.props.yUnit &&
      this.props.yUnit !== prevProps.yUnit &&
      this.props.yUnit !== this.state.yUnit) {
      this.setState({ yUnit: this.props.yUnit });
    }

    // Controlled-units mode: shaft-speed changes flow through the parent's
    // derivation (data/features arrive re-converted), so no local re-transform.
    if (!this.props.controlledUnits &&
      prevProps.shaftSpeed && this.props.shaftSpeed && !_.isEqual(prevProps.shaftSpeed, this.props.shaftSpeed)) {
      const { xUnit } = this.props;
      if (xUnit === 'Orders') {
        this.setState({
          features: this.getFeatures(),
        }, this.toggleXaxisUnits('Orders'));
      }
    }

    if (!_.isEqual(prevState.features, this.state.features)) {
      this.drawFeatures();
    }
    if (this.props.excluded_features && !_.isEqual(prevProps.excluded_features, this.props.excluded_features)) {
      this.drawFeatures();
      this.highlightSelectedFeature();
    }
    if (this.props.measurementTimestamp && !_.isEqual(prevProps.measurementTimestamp, this.props.measurementTimestamp)) {
      this.setState({ features: this.getFeatures() });
      this.drawFeatures();
    }
    if (!_.isEqual(prevState.dataToDisplay, this.state.dataToDisplay)) {
      this.highlightSelectedFeature();
    }
  }

  // ADDED: Debounced feature redraw to prevent excessive calls
  debouncedDrawFeatures = _.debounce(() => {
    if (this.ctx) {
      this.drawFeatures();
    }
  }, 16); // 16ms = ~60fps

  scaleEnvelopeIfRequired = () => {
    const { spectralEnvelope, envelopeXUnit, xUnit, referenceShaftSpeed } = this.state;
    const { shaftSpeedUnit } = this.props;

    // Early return if no scaling needed
    if (!this.shouldScaleEnvelope(spectralEnvelope, envelopeXUnit, xUnit)) return;

    const shaft_speed = referenceShaftSpeed || this.getShaftSpeed();
    const newEnvelopeXUnit = this.getNewEnvelopeXUnit(spectralEnvelope, envelopeXUnit, xUnit);

    // Skip scaling if Orders conversion requires shaft speed but we don't have it
    if (!shaft_speed && this.requiresShaftSpeed(xUnit, newEnvelopeXUnit)) {
      return;
    }

    const newEnvelope = getScaledEnvelope(
      spectralEnvelope,
      newEnvelopeXUnit,
      xUnit,
      shaft_speed,
      shaftSpeedUnit
    );

    this.setState({
      spectralEnvelope: newEnvelope,
      envelopeXUnit: xUnit,
      referenceShaftSpeed: shaft_speed
    }, () => {
      if (!this.props.showOverlay) {
        this.drawEnvelope();
      }
    });
  }

  shouldScaleEnvelope = (spectralEnvelope, envelopeXUnit, xUnit) => {
    if (envelopeXUnit !== xUnit) return true;

    const envelopeData = spectralEnvelope && spectralEnvelope.envelope_data;
    if (!envelopeData || !envelopeData.x_scale || typeof envelopeData.x_scale !== 'string') {
      return false;
    }

    return envelopeData.x_scale.toLowerCase() !== String(xUnit).toLowerCase();
  }

  getNewEnvelopeXUnit = (spectralEnvelope, envelopeXUnit, xUnit) => {
    if (envelopeXUnit !== xUnit) return envelopeXUnit;

    const xScale = spectralEnvelope.envelope_data.x_scale;
    return xScale.charAt(0).toUpperCase() + xScale.slice(1);
  }

  requiresShaftSpeed = (xUnit, newEnvelopeXUnit) => xUnit === 'Orders' || newEnvelopeXUnit === 'Orders'

  allowYUnitToggle = () => {
    const { tag_type, updateEnvelopeData, toggleSpectrumAmplitudeUnits } = this.props;
    if ((tag_type === 'vibration') && !updateEnvelopeData && toggleSpectrumAmplitudeUnits) return true;
    return false;
  }

  getShaftSpeed = () => {
    const {
      shaftSpeed: spectrumShaftSpeed,
      componentNode,
      measurementTimestamp,
      initialXunit,
      setShaftSpeedState,
      features,
      useSpectrumShaftSpeed
    } = this.props;

    const shaftSpeed = getShaftSpeed(spectrumShaftSpeed, useSpectrumShaftSpeed, componentNode, measurementTimestamp, initialXunit, features);
    if (setShaftSpeedState) setShaftSpeedState(shaftSpeed);
    return shaftSpeed;
  }

  /**
   * Determines if a unit change represents an amplitude type change
   * @param {string} fromUnit - Previous Y-axis unit
   * @param {string} toUnit - New Y-axis unit
   * @returns {boolean} - True if this is an amplitude type change
   */
  isAmplitudeTypeChange = (fromUnit, toUnit) => {
    if (!fromUnit || !toUnit) return false;

    const fromType = this.getAmplitudeTypeFromUnit(fromUnit);
    const toType = this.getAmplitudeTypeFromUnit(toUnit);

    return fromType !== toType;
  };

  /**
   * Gets amplitude type from Y-axis unit
   * @param {string} unit - Y-axis unit
   * @returns {string} - Amplitude type
   */
  getAmplitudeTypeFromUnit = (unit) => {
    if (!unit) return 'unknown';

    const normalizedUnit = unit.toLowerCase().trim();

    // Velocity indicators
    if (normalizedUnit.includes('mm/s') || normalizedUnit.includes('in/s') ||
      normalizedUnit.includes('velocity') || normalizedUnit.includes('v ')) {
      return 'velocity';
    }

    // Acceleration indicators (including g-force)
    if (normalizedUnit.includes('g') || normalizedUnit.includes('acceleration') ||
      normalizedUnit.includes('a ') || normalizedUnit.includes('m/s²') ||
      normalizedUnit.includes('in/s²')) {
      return 'acceleration';
    }

    // Demodulation indicators
    if (normalizedUnit.includes('demod') || normalizedUnit.includes('envelope') ||
      (normalizedUnit.includes('a') && normalizedUnit.length < 3)) {
      return 'demod';
    }

    // Displacement indicators
    if (normalizedUnit.includes('mm') || normalizedUnit.includes('mil') ||
      normalizedUnit.includes('displacement') || normalizedUnit.includes('µm')) {
      return 'displacement';
    }

    // dB units (can be any amplitude type in logarithmic scale)
    if (normalizedUnit.includes('db')) {
      return 'db';
    }

    return 'unknown';
  };

  getComponentLocalCustomMarkers = () => {
    const localCustomMarkers = JSON.parse(localStorage.getItem('custom_markers'));
    const { componentNode } = this.props;
    if (!componentNode || !localCustomMarkers || !componentNode.id) return null;
    return localCustomMarkers[componentNode.id];
  }

  getDataInCorrectYunit = (newYUnit) => {
    const { initialYunit, originalData, data } = this.props;
    const reference = getAmplitudeConversionReference(initialYunit, newYUnit);
    if (!reference) return originalData.map((dataset, dataidx) => dataset.map((d, didx) => ({
      x: data[dataidx] && data[dataidx][didx] ? data[dataidx][didx].x : d.x,
      y: d.y
    })));
    let spectrumData = null;
    if (newYUnit.toLowerCase() === 'db') {
      spectrumData = originalData.map((dataset, dataidx) => dataset.map((d, didx) => ({
        x: data[dataidx] && data[dataidx][didx] ? data[dataidx][didx].x : d.x,
        y: convertToDbScale(d.y, reference)
      })));
    } else if (initialYunit.toLowerCase() === 'db') {
      spectrumData = originalData.map((dataset, dataidx) => dataset.map((d, didx) => ({
        x: data[dataidx] && data[dataidx][didx] ? data[dataidx][didx].x : d.x,
        y: convertFromDbScale(d.y, reference)
      })));
    }
    return spectrumData;
  }

  getFeatures = () => {
    const features = _.cloneDeep(this.props.features);
    if (!this.props.componentNode) return features;
    const { componentNode: { node_details: { component_type } } } = this.props;

    if (!this.props.measurementTimestamp) return features;
    const measurementTimestampInISO = moment(this.props.measurementTimestamp).toISOString();

    const componentLocalMarkers = this.getComponentLocalCustomMarkers();
    if (!componentLocalMarkers || !componentLocalMarkers[measurementTimestampInISO]) {
      if (!features) return { items: [] };
      return features;
    }

    const newPeakValue = componentLocalMarkers[measurementTimestampInISO].speed;

    let isSpeedMarkerPresent = false;

    features.items.forEach((feature) => {
      if (feature.feature_type === 'forcing_frequencies' && feature.multiplier === 1) {
        isSpeedMarkerPresent = true;
      }
    });

    const unitConversionFactor = getFreqConversionFactor('Hz', this.props.xUnit, newPeakValue);

    if (!isSpeedMarkerPresent) {
      // set this so that new marker is updated
      // Create new feature marker from template
      const marker = {
        marker: `1${getComponentTypeShortHand(component_type)}`,
        frequency: newPeakValue * unitConversionFactor,
        freq_units: this.props.xUnit,
        name: 'Shaft Speed',
        description: 'Shaft speed',
        type: 'individual',
        user_id: null,
        tag_id: null,
        feature_type: 'forcing_frequencies',
      };
      features.items.push(marker);
    }

    features.items = features.items.map((feature) => {
      if ((feature.feature_type === 'forcing_frequencies') && !markersToSkip.has(feature.marker) && !_.isNil(feature.multiplier)) {
        feature.frequency = newPeakValue * unitConversionFactor * feature.multiplier;
        feature.freq_units = this.props.xUnit;
      }
      return feature;
    });

    return features;
  }

  calculateFrequenciesContribution = () => {
    const { fmax, lines_of_resolution, originalData } = this.props;
    const shaftSpeed = this.getShaftSpeed();
    if (!shaftSpeed || !lines_of_resolution) return;
    const offset = 2 * fmax / lines_of_resolution;
    let sync = 0;
    let subSync = 0;
    let nonSync = 0;
    originalData[0].forEach((dataPoint) => {
      const freq = _.round(dataPoint.x, 1);
      const amplitude = dataPoint.y;
      const remainder = freq % shaftSpeed;
      if (remainder <= offset || (shaftSpeed - remainder) <= offset) { // frequency lies in offset region
        sync += (amplitude ** 2);
      } else if (freq < shaftSpeed) subSync += (amplitude ** 2);
      else {
        nonSync += (amplitude ** 2);
      }
    });
    sync = Math.sqrt(sync);
    subSync = Math.sqrt(subSync);
    nonSync = Math.sqrt(nonSync);
    const totalAmplitudeSum = sync + subSync + nonSync;
    this.setState({
      frequencyContribution: {
        sync: _.round(sync / totalAmplitudeSum * 100, 1),
        subSync: _.round(subSync / totalAmplitudeSum * 100, 1),
        nonSync: _.round(nonSync / totalAmplitudeSum * 100, 1),
        totalAmplitudeSum
      }
    });
  };

  selectedFrequencyContribution = () => {
    const { fmax, lines_of_resolution, originalData } = this.props;
    if (!lines_of_resolution || _.isEmpty(originalData)) return 0;
    const factor = getFreqConversionFactor(this.state.xUnit, 'Hz', this.props.shaftSpeed, this.props.initialXunit);
    const selectedFrequency = this.getXDataPoint() * factor;
    const offset = 2 * fmax / lines_of_resolution;
    let selectedFreqContribution = 0;
    let peakAmplitudeForHarmonic = 0;
    originalData[0].forEach((dataPoint) => {
      const freq = _.round(dataPoint.x, 1);
      const amplitude = _.round(dataPoint.y, 3);
      const remainder = freq % selectedFrequency;
      if (remainder <= offset || (selectedFrequency - remainder) <= offset) {
        peakAmplitudeForHarmonic = _.max([peakAmplitudeForHarmonic, amplitude]);
      } else {
        selectedFreqContribution += peakAmplitudeForHarmonic;
        peakAmplitudeForHarmonic = 0;
      }
    });
    selectedFreqContribution += peakAmplitudeForHarmonic;
    return _.round(selectedFreqContribution / this.state.frequencyContribution.totalAmplitudeSum * 100, 0);
  }

  setNewPeak = () => {
    const { componentNode } = this.props;
    if (!componentNode) return;
    const localCustomMarkers = JSON.parse(localStorage.getItem('custom_markers')) || {};
    const componentLocalMarkers = this.getComponentLocalCustomMarkers() || {};

    if (!this.props.measurementTimestamp) return;

    const measurementTimestampInISO = moment(this.props.measurementTimestamp).toISOString();

    const selection = this.ctx.props.selection;
    let speed = _.isArray(selection) ? selection[0].x : selection.x;

    const factor = getFreqConversionFactor(this.state.xUnit, 'Hz', this.props.shaftSpeed, this.props.initialXunit);
    // speed is always stored in HZ
    speed *= factor;

    componentLocalMarkers[measurementTimestampInISO] = { speed };
    localCustomMarkers[componentNode.id] = componentLocalMarkers;
    localStorage.setItem('custom_markers', JSON.stringify(localCustomMarkers));

    this.getShaftSpeed();
    // Close the Modal and set new features
    this.setState({
      showNewPeakModal: false,
      features: this.getFeatures()
    });
  }

  setHarmonicNumber = (e) => {
    this.setState({ harmonicNumber: parseInt(e.target.value, 10) });
  }

  updateHarmonicFrequencies = () => {
    if (this.state.harmonicNumber < 1 || this.state.harmonicNumber > 500 || _.isNaN(this.state.harmonicNumber)) {
      this.setState({
        harmonicNumber: parseInt('1', 10),
        showNewPeakModal: false
      });
      return;
    }

    if (this.state.cursorType) this.disableCursors();

    const p = [
      _.isArray(this.ctx.props.selection) ? _.cloneDeep(this.ctx.props.selection[0]) : _.cloneDeep(this.ctx.props.selection),
      _.isArray(this.ctx.props.selection) ? _.cloneDeep(this.ctx.props.selection[0]) : _.cloneDeep(this.ctx.props.selection)
    ];
    const tagged = this.annotateSpectrumCursorPoints(p, this.getActiveSpectrumUnit());
    this.props.setPointsToCorrectUnits(tagged);
    this.setState(
      {
        points: tagged
      }, this.setNthHarmonic
    );
  }

  // keyCode left: 37, right: 39
  handleKeyStrokes = (e) => {
    if (!this.ctx || !this.ctx.chart || !this.ctx.x || this.props.lastHoveredChart !== this.props.type) return;

    if (e.keyCode === KEYBOARD_CODES.H && !this.props.split) {
      const p = [
        _.isArray(this.ctx.props.selection) ? _.cloneDeep(this.ctx.props.selection[0]) : _.cloneDeep(this.ctx.props.selection),
        _.isArray(this.ctx.props.selection) ? _.cloneDeep(this.ctx.props.selection[0]) : _.cloneDeep(this.ctx.props.selection)
      ];
      const tagged = this.annotateSpectrumCursorPoints(p, this.getActiveSpectrumUnit());
      this.props.setPointsToCorrectUnits(tagged);
      this.setState(
        {
          harmonicNumber: 1,
          points: tagged
        },
        this.setNthHarmonic
      );
    }

    if (e.keyCode === KEYBOARD_CODES.ENTER && this.state.showNewPeakModal) {
      this.updateHarmonicFrequencies();
    }

    if (e.keyCode === KEYBOARD_CODES.S) {
      if (!this.state.cursorType) {
        this.ctx.props.mouseClick();

        if (this.state.points.length === 0) return;

        const x1 = this.state.points[0].x;
        const indexR = this.state.points[0].indexOnDataset;

        // Check if data exists and has the expected structure
        if (!this.ctx.props.data || !Array.isArray(this.ctx.props.data) || !this.ctx.props.data[0]) {
          return;
        }

        let nextPoint = null;
        let nextIndex = indexR;
        // Add safeguards against infinite loops and array bounds errors
        const maxIterations = 1000; // Reasonable limit to prevent infinite loops
        let iterations = 0;

        while (!nextPoint && iterations < maxIterations) {
          iterations++;
          nextIndex += 1;

          // Make sure we don't go beyond array bounds
          if (nextIndex >= this.ctx.props.data[0].length) {
            break;
          }

          // Make sure the data point at this index exists
          if (!this.ctx.props.data[0][nextIndex]) {
            // eslint-disable-next-line no-continue
            continue;
          }

          // Safe access to properties
          const dataPoint = this.ctx.props.data[0][nextIndex];
          if (dataPoint && typeof dataPoint.x !== 'undefined' &&
            this.ctx.x(dataPoint.x - x1) >= X_UNITS_MIN_PIXEL_DIFFERENCE) {
            nextPoint = dataPoint;
          }
        }

        // If we couldn't find a suitable next point, exit
        if (!nextPoint) {
          return;
        }

        this.drawCursors(this.ctx, nextPoint, 'sideband');
        nextPoint.indexOnDataset = nextIndex;
        const nextTagged = { ...nextPoint, spectrumFreqUnit: this.getActiveSpectrumUnit() };
        this.setState({
          dataPoint: nextTagged,
          cursorType: 'sideband'
        });

        this.drawCursorInfotext(this.ctx, nextTagged, 'sideband');
        this.ctx.props.mouseClick();
      } else if (_.isEqual(this.state.cursorType, 'sideband')) {
        // sidebands already enabled, disable it
        this.ctx.props.mouseClick();
        this.disableCursors();
      }
    }

    if (e.keyCode !== KEYBOARD_CODES.LEFT_ARROW && e.keyCode !== KEYBOARD_CODES.RIGHT_ARROW) return;
    const isVisible = isElementPartiallyVisible(this.ctx.chart);
    if (!isVisible) return;
    const { dataPoint, cursorType, points } = _.cloneDeep(this.state);
    const { data } = this.props;

    // Check if data exists and has the expected structure
    if (!data || !Array.isArray(data) || !data[0] || !Array.isArray(data[0])) {
      return;
    }

    if (cursorType === 'sideband') {
      const { indexOnDataset } = dataPoint;
      const { indexOnDataset: referenceIndex } = points[0];
      let newIndex = -1;
      if (e.keyCode === KEYBOARD_CODES.LEFT_ARROW) {
        if (Math.abs(indexOnDataset - referenceIndex) < 10) return;
        if (indexOnDataset < referenceIndex) newIndex = indexOnDataset + 1;
        else newIndex = indexOnDataset - 1;

        if (newIndex < 0 || newIndex > data[0].length - 1) return;
        dataPoint.x = data[0][newIndex].x;
        dataPoint.indexOnDataset = newIndex;
      } else if (e.keyCode === KEYBOARD_CODES.RIGHT_ARROW) {
        if (indexOnDataset < referenceIndex) newIndex = indexOnDataset - 1;
        else newIndex = indexOnDataset + 1;

        if (newIndex < 0 || newIndex > data[0].length - 1) return;
        dataPoint.x = data[0][newIndex].x;
        dataPoint.indexOnDataset = newIndex;
      }

      const diffXvalue = Math.abs(dataPoint.x - this.state.points[0].x);
      dataPoint.text = Math.round(diffXvalue);
      if (!dataPoint.spectrumFreqUnit) dataPoint.spectrumFreqUnit = this.getActiveSpectrumUnit();

      this.setState({ dataPoint });
      this.drawCursors(this.ctx, dataPoint, cursorType);
      this.drawCursorInfotext(this.ctx, dataPoint, cursorType);
    } else if (cursorType === 'harmonic') {
      const { indexOnDataset } = points[0];
      let newIndex = -1;
      if (e.keyCode === KEYBOARD_CODES.LEFT_ARROW) {
        newIndex = indexOnDataset - 1;
        if (newIndex < 1) return;
        points.forEach((p) => {
          p.x = data[0][newIndex].x;
          p.indexOnDataset = newIndex;
          p.y = data[0][newIndex].y;
        });
      } else if (e.keyCode === KEYBOARD_CODES.RIGHT_ARROW) {
        newIndex = indexOnDataset + 1;
        if (newIndex > data[0].length) return;
        points.forEach((p) => {
          p.x = data[0][newIndex].x;
          p.indexOnDataset = newIndex;
          p.y = data[0][newIndex].y;
        });
      }
      const pointsTagged = this.annotateSpectrumCursorPoints(points, this.getActiveSpectrumUnit());
      this.props.setPointsToCorrectUnits(pointsTagged);
      this.setState({ points: pointsTagged },
        () => {
          this.drawCursors(this.ctx, dataPoint, cursorType);
          this.drawCursorInfotext(this.ctx, pointsTagged[0], cursorType);
        });
    } else {
      const direction = e.keyCode === KEYBOARD_CODES.LEFT_ARROW ? 'left' : 'right';
      let selections = this.ctx.props.selection;

      if (!_.isArray(selections)) {
        selections = [selections];
      }

      const newSelections = selections.map((selection, idx) => {
        const dataset = this.ctx.props.data[idx];
        return ({ ...selection, ...helpers.getNextDataPoint(dataset, selection.x, direction) });
      });

      this.ctx.props.mouseover(newSelections.length > 1 ? newSelections : newSelections[0]);
    }
  }

  /* eslint-disable */
  drawSelection(ctx, selection) {
    d3.selectAll(`.${ctx.props.chartName}.selection`).remove();
    let selections = [];
    if (!_.isArray(selection)) selections = [_.cloneDeep(selection)]
    else selections = selection
    const height = ctx.chart.getBoundingClientRect().height
    selections.forEach((dataPoint) => {
      graphUtils.drawLineOnPos(ctx.chart, ctx.x(dataPoint.x), 0, ctx.x(dataPoint.x), height, `${ctx.props.chartName} selection`)
        .attr('pointer-events', 'none');
    });

    selections.forEach((dataPoint) => {
      graphUtils.drawCircleOnPos(ctx.chart, ctx.x(dataPoint.x), ctx.y(dataPoint.y), 3, `${ctx.props.chartName} selection`)
        .attr('pointer-events', 'none')
        .attr('fill', ctx.props.colors ? ctx.props.colors[dataPoint.dataIdx] : colors.green);
    });
  }

  drawEnvelope = () => {
    if (!this.ctx) return;
    const { chartName } = this.props;
    const { spectralEnvelope } = this.state;
    if (!spectralEnvelope || !spectralEnvelope.envelope_data) return;
    d3.selectAll(`.${chartName}.step-graph.dataline`).remove();
    const { start_frequencies, end_frequencies, amplitudes } = spectralEnvelope.envelope_data;

    graphUtils.drawStepGraph(
      start_frequencies,
      end_frequencies,
      amplitudes,
      chartName,
      this.ctx.chart,
      this.ctx.x,
      this.ctx.y
    );
  }

  drawEnvelopeToolTip = (point) => {
    const { chartName } = this.props;
    d3.selectAll(`.${chartName}.step-graph.circle`).remove();
    if (!this.ctx || !point || !point.y) return;
    const xPos = this.ctx.x(point.x);
    if (xPos > 0 && xPos < this.ctx.chart.getBoundingClientRect().width) {
      graphUtils.drawCircleOnPos(
        this.ctx.chart,
        xPos,
        this.ctx.y(point.y),
        3,
        `${chartName} step-graph`
      ).attr('pointer-events', 'none');
    }
  }

  drawGraph(ctx) {
    if (this.cursors) {
      this.redrawCursors(ctx);
    }

    this.ctx = ctx;
    this.cursorContainer = d3.select(ctx.chart)
      .append('g')
      .attr('width', ctx.chart.getBoundingClientRect().width)
      .attr('height', ctx.chart.getBoundingClientRect().height)
      .attr('class', `${this.props.chartName} cursorGroupContainer`);

    this.featureContainer = d3.select(ctx.chart)
      .append('g')
      .attr('width', ctx.chart.getBoundingClientRect().width)
      .attr('height', ctx.chart.getBoundingClientRect().height)
      .attr('class', `${this.props.chartName} featureGroupContainer`);

    this.drawFeatures();
    this.addDoubleClickEventListener(ctx);
    this.addRightMouseClickEvent(ctx);
    if (this.props.spectralEnvelope && !this.props.showOverlay) this.drawEnvelope();
  }

  drawPoints(ctx, points) {
    points.map(point => helpers.closestXDataPointOnDataset(ctx.props.data[0], point.x))
    const annotated = this.annotateSpectrumCursorPoints(points, this.getActiveSpectrumUnit());
    if (points.length === 1) {
      this.drawCursor(ctx, { ...annotated[0], text: 'R' }, 'sideband');
      this.setState({
        points: annotated,
        drawingCursor: true
      });
    } else if (points.length === 2) {
      this.setState({
        points: annotated,
        drawingCursor: false
      });
    } else if (points.length === 0) {
      this.disableCursors();
    }
  }

  disableCursors = () => {
    this.cursors = [];
    d3.selectAll(`.${this.props.chartName}.cursorGroup`).remove();
    d3.selectAll(`.${this.props.chartName}.cursorInfo`).remove();
    this.setState({
      points: [],
      drawingCursor: false,
      dataPoint: {},
      cursorType: null
    });
  }

  /** Authoritative X-axis unit for the spectrum chart — always prefer local state over props during toggle transitions */
  getActiveSpectrumUnit = () => this.state.xUnit || this.props.xUnit || 'Hz';

  /** Tag cursor points with the unit their x values are stored in */
  annotateSpectrumCursorPoints = (pts, spectrumFreqUnit) => {
    if (!pts || !Array.isArray(pts) || !spectrumFreqUnit) return pts;
    return pts.map((p) => ({ ...p, spectrumFreqUnit }));
  }

  /** Convert a single x value between frequency units, with safe fallbacks */
  convertCursorX = (value, fromUnit, toUnit) => {
    if (value == null || typeof value !== 'number' || !Number.isFinite(value)) return value;
    if (!fromUnit || !toUnit || fromUnit === toUnit) return value;
    const backendFrequencyUnit = this.props.initialXunit || 'Hz';
    const shaft = this.state.referenceShaftSpeed || this.getShaftSpeed();
    const factor = getFreqConversionFactor(fromUnit, toUnit, shaft, backendFrequencyUnit);
    if (!Number.isFinite(factor) || factor <= 0) return value;
    return value * factor;
  }

  /** Scale a cursor/point object's x coordinate from one unit to another, preserving other fields */
  convertCursorPoint = (point, fromUnit, toUnit) => {
    if (!point || fromUnit === toUnit) return point;
    return {
      ...point,
      x: this.convertCursorX(point.x, fromUnit, toUnit),
      spectrumFreqUnit: toUnit
    };
  }

  /**
   * Compute the display label for a sideband cursor.
   * Returns 'R' for the reference, null to hide (sub-1X Orders), or the integer distance label.
   */
  getSidebandLabel = (refX, dpX, cursorX, displayUnit) => {
    const sidebandWidth = Math.abs(dpX - refX);
    if (!Number.isFinite(sidebandWidth) || sidebandWidth <= 0) return undefined;

    const distanceFromReference = cursorX - refX;
    if (Math.abs(distanceFromReference) < sidebandWidth * 0.5) return 'R';

    const multiple = Math.round(distanceFromReference / sidebandWidth);
    const label = Math.round(sidebandWidth * multiple);

    if (displayUnit === 'Orders' && sidebandWidth < 1 && Math.abs(label) < 1) return null;
    return label;
  }

  getRedrawnCursor = (cursor) => {
    if (this.state.cursorType !== 'sideband') {
      return cursor;
    }

    const referencePoint = this.getReferencePoint();
    const { dataPoint } = this.state;
    if (!referencePoint || !dataPoint || typeof dataPoint.x !== 'number') {
      return cursor;
    }

    const displayUnit = this.getActiveSpectrumUnit();
    const refX = this.convertCursorX(referencePoint.x, referencePoint.spectrumFreqUnit || displayUnit, displayUnit);
    const dpX = this.convertCursorX(dataPoint.x, dataPoint.spectrumFreqUnit || displayUnit, displayUnit);
    const cursorX = this.convertCursorX(cursor.x, cursor.spectrumFreqUnit || displayUnit, displayUnit);

    const label = this.getSidebandLabel(refX, dpX, cursorX, displayUnit);
    if (label === undefined) return cursor;
    if (label === null) return null;
    return { ...cursor, text: label };
  }

  redrawCursors(ctx) {
    if (!this.cursors || !Array.isArray(this.cursors) || this.cursors.length === 0) {
      return;
    }

    d3.selectAll(`.${this.props.chartName}.cursorGroup`).remove();

    // Cache chart bounds to avoid repeated DOM queries
    const chartBounds = ctx.chart.getBoundingClientRect();
    const chartWidth = chartBounds.width;

    this.cursors.forEach((cursor) => {
      if (!cursor || typeof cursor.x !== 'number' || isNaN(cursor.x)) {
        return;
      }

      const cursorXPos = ctx.x(cursor.x);

      if (cursorXPos < 0 || cursorXPos > chartWidth) {
        return;
      }

      const redrawnCursor = this.getRedrawnCursor(cursor);
      if (!redrawnCursor) {
        return;
      }

      this.drawCursor(ctx, redrawnCursor, this.state.cursorType);
    });
  }

  drawCursorInfotext(ctx, dataPoint, cursorType) {
    const { harmonicNumber } = this.state;
    const data = dataPoint.x / harmonicNumber;

    // Always match the chart axis — do not use point.xUnit (often backend Hz) or the optional
    // xUnit arg, or Reference/Sideband labels stay in Hz when the axis is CPM or Orders.
    const currentUnit = this.getActiveSpectrumUnit();
    const isOrdersUnit = currentUnit === 'Orders';

    // Precision constants - use 2 decimals for all units
    const PRECISION = 2;

    // Hz / CPM show the unit name; Orders show a lowercase x suffix (e.g. 3.38x)
    const formatWithUnit = (value, precision) =>
      isOrdersUnit ? `${toFixedXIfNumber(value, precision)}x`
        : `${toFixedXIfNumber(value, precision)} ${currentUnit}`;

    const titleToDisplay = i18n.t('machineDashboard.health.charts.cursors.harmonicsOf', {
      value: formatWithUnit(data, PRECISION),
      defaultValue: `Harmonics of ${formatWithUnit(data, PRECISION)}`,
    });

    d3.selectAll(`.${this.props.chartName}.cursorInfo`).remove();
    let featureSpace = 0;
    if (this.props.features && this.props.features.items && this.props.features.items.length > 0) {
      featureSpace = -16;
    }
    if (cursorType === 'sideband') {
      if (!this.state.points || !this.state.points[0]) {
        return;
      }

      const refPt = this.state.points[0];
      const refDisplay = this.convertCursorX(refPt.x, refPt.spectrumFreqUnit || currentUnit, currentUnit);
      const dpDisplay = this.convertCursorX(dataPoint.x, dataPoint.spectrumFreqUnit || currentUnit, currentUnit);

      const referenceText = i18n.t('machineDashboard.health.charts.cursors.reference', {
        value: formatWithUnit(this.state.points[0].x, PRECISION),
        defaultValue: `Reference: ${formatWithUnit(refDisplay, PRECISION)}`,
      });

      d3.select(ctx.chart)
        .append('text')
        .attr('class', `${this.props.chartName} cursorInfo`)
        .attr('dx', 0)
        .attr('dy', -20 + featureSpace)
        .text(referenceText);

      const sidebandText = i18n.t('machineDashboard.health.charts.cursors.sideband', {
        value: formatWithUnit(Math.abs(dataPoint.x - this.state.points[0].x), PRECISION),
        defaultValue: `Sideband: ${formatWithUnit(Math.abs(dpDisplay - refDisplay), PRECISION)}`,
      });

      d3.select(ctx.chart)
        .append('text')
        .attr('class', `${this.props.chartName} cursorInfo`)
        .attr('dx', 0)
        .attr('dy', -5 + featureSpace)
        .text(sidebandText);
    } else if (cursorType === 'harmonic') {
      d3.select(ctx.chart)
        .append('text')
        .attr('class', `${this.props.chartName} cursorInfo`)
        .attr('dx', 0)
        .attr('dy', -5 + featureSpace)
        .text(titleToDisplay);
    }
  }

  addMouseoverEventListener(ctx) {
    if (this.mouseover) return;
    // this is really slow on linechart
    this.mouseover = graphUtils.addMouseoverEvent(ctx.chart, () => {
      if (this.state.showNewPeakModal) return;
      const dataPoint = helpers.closestExactDataPoint(ctx);
      if (!dataPoint) return;

      if (this.state.drawingCursor) {
        this.drawCursors(ctx, dataPoint, 'sideband');
        const dataPointTagged = { ...dataPoint, spectrumFreqUnit: this.getActiveSpectrumUnit() };
        this.setState({
          dataPoint: dataPointTagged,
          cursorType: 'sideband'
        });
        this.drawCursorInfotext(ctx, dataPointTagged, 'sideband');
      } else {
        const { spectralEnvelope } = this.state;
        const selection = { ...dataPoint };
        if (spectralEnvelope && spectralEnvelope.envelope_data) {
          const envelopeDataPoint = helpers.getClosestStepGraphPoint(
            spectralEnvelope.envelope_data,
            dataPoint.x
          );
          this.drawEnvelopeToolTip(envelopeDataPoint);
          if (!_.isEmpty(envelopeDataPoint)) {
            selection.envelopeAmplitude = envelopeDataPoint.y;
          }
        }
        ctx.props.mouseover(selection, this.props.chartName);
      }
    });
  }

  addDoubleClickEventListener(ctx) {
    if (this.doubleClick && !this.props.doubleClick) return;

    this.doubleClick = graphUtils.addDoubleClickEvent(ctx.chart, () => {
      const p = [
        _.isArray(this.ctx.props.selection) ? _.cloneDeep(this.ctx.props.selection[0]) : _.cloneDeep(this.ctx.props.selection),
        _.isArray(this.ctx.props.selection) ? _.cloneDeep(this.ctx.props.selection[0]) : _.cloneDeep(this.ctx.props.selection)
      ];
      const tagged = this.annotateSpectrumCursorPoints(p, this.getActiveSpectrumUnit());
      this.props.setPointsToCorrectUnits(tagged);
      this.setState(
        {
          harmonicNumber: 1,
          points: tagged
        }, this.setNthHarmonic
      )
    });
  }

  addRightMouseClickEvent(ctx) {
    // Use shared right-click utility for consistency
    addOutlierRightClickEvent(ctx, {
      enableRightClick: true, // SpectrumChart always allows right-click for peak modal
      component: 'spectrum',
      onRightClick: (dataPos, selection) => {
        this.setState({
          dataPos,
          showNewPeakModal: true
        });
      }
    });
  }

  /**
   * Gets the reference point for cursor operations.
   * Prioritizes state points over props points to ensure consistency during transformations.
   *
   * @returns {Object|null} The reference point object with x, y coordinates and metadata,
   *                        or null if no valid reference point is available
   */
  getReferencePoint = () => {
    // Prefer state points, fallback to props points
    if (this.state.points && this.state.points.length > 0) {
      return this.state.points[0];
    }
    if (this.ctx && this.ctx.props && this.ctx.props.points && this.ctx.props.points.length > 0) {
      return this.ctx.props.points[0];
    }
    return null;
  }

  validateCursorOperation = () => {
    if (!this.ctx || !this.ctx.chart || !this.ctx.x) {
      return false;
    }

    const referencePoint = this.getReferencePoint();
    if (!referencePoint) {
      return false;
    }

    if (!this.state.harmonicNumber || this.state.harmonicNumber <= 0) {
      return false;
    }

    return true;
  }

  setNthHarmonic = () => {
    if (!this.state.cursorType) {
      if (!this.validateCursorOperation()) return;

      const referencePoint = this.getReferencePoint();
      const dataPointRaw = helpers.closestXDataPointOnDataset(this.ctx.props.data[0], 0);
      const dataPoint = { ...dataPointRaw, spectrumFreqUnit: this.getActiveSpectrumUnit() };
      this.drawCursors(this.ctx, dataPoint, 'harmonic');
      this.drawCursorInfotext(this.ctx, referencePoint, 'harmonic');
      this.setState({
        drawingCursor: false,
        dataPoint,
        cursorType: 'harmonic',
      });
    } else if (_.isEqual(this.state.cursorType, 'harmonic')) {
      this.disableCursors();
    }

    this.setState({
      showNewPeakModal: false
    });
  }

  drawCursor(ctx, point, cursorType = null) {
    if (!cursorType) return;

    const points = ctx.props.data.map((dataSet) => {
      if (cursorType === 'harmonic') {
        const yValue = helpers.interpolateYDataPointOnDataset(dataSet, point.x);
        return {
          ...point,
          y: yValue.y
        }
      }
      return helpers.closestXDataPointOnDataset(dataSet, point.x);
    });

    const cursorG = this.cursorContainer
      .append('g')
      .attr('class', `${this.props.chartName} cursorGroup`)
      .attr('transform', `translate(${ctx.x(point.x)}, 0)`);

    graphUtils.drawLineOnPos(cursorG.node(), 0, 0, 0, ctx.chart.getBoundingClientRect().height, `${this.props.chartName} cursorSelection`)
      .attr('stroke-width', 3)
      .attr('stroke-dasharray', (5, 5));

    cursorG.append('text')
      .attr('class', `${this.props.chartName} cursorValue`)
      .attr('dx', 3)
      .attr('dy', 12)
      .text(point.text || toFixedXIfNumber(point.x, 0));

    points.map((point, idx) => graphUtils.drawCircleOnPos(cursorG.node(), 0, ctx.y(point.y), 4, `${this.props.chartName} cursorSelection`)
      .attr('fill', ctx.props.colors ? ctx.props.colors[idx] : colors.green));
  }

  get1xMarkerFrequency() {
    const features = this.state.features.items;
    const feature = features.find(feature => feature.marker === '1x');
    return feature ? feature.frequency : -1;
  }

  drawCursors(ctx, point, cursorType) {
    // calculate distance of hover position (point) to the first clicked position (this.props.points[0])
    if (!cursorType) return;

    const referencePoint = this.getReferencePoint();
    if (!referencePoint) {
      return;
    }

    let shaftSpeed = this.getShaftSpeed();
    let peakSearchThreshold = 60;
    const stfactor = getFreqConversionFactor('Hz', this.state.xUnit, shaftSpeed, this.state.initialXunit);
    peakSearchThreshold *= stfactor;

    const { features, componentNode } = this.props;
    if (features) {
      const Marker1X = features.items.find(
        feature => feature.marker === `1${componentNode ? getComponentTypeShortHand(componentNode.node_details.component_type) : 'X'}`
      );
      if (Marker1X) {
        peakSearchThreshold = Marker1X.frequency;
        const stfactor = getFreqConversionFactor(this.props.initialXunit, this.state.xUnit, shaftSpeed, this.props.initialXunit);
        peakSearchThreshold *= stfactor;
      }
    }

    const factor = getFreqConversionFactor(this.props.initialXunit, this.state.xUnit, shaftSpeed, this.props.initialXunit);
    shaftSpeed *= factor;

    const { harmonicNumber, xUnit } = this.state;
    let diffXvalue;
    if (cursorType === 'harmonic') {
      diffXvalue = helpers.interpolatePeakFrequency(referencePoint, ctx.props.data, xUnit) / harmonicNumber;
    } else {
      diffXvalue = Math.abs((point.x - referencePoint.x) / harmonicNumber);
    }
    const diffXcoord = Math.abs(ctx.x((point.x - referencePoint.x) / harmonicNumber));
    let initialCursorText = cursorType === 'sideband' ? 'R' : harmonicNumber;
    let harmonicsMarkerSuffix = '';
    let harmonicsStartPoint;
    const selectedPoint = {
      ...referencePoint,
      x: referencePoint.x / harmonicNumber
    }

    if (cursorType === 'harmonic') {
      const peakFrequency = this.get1xMarkerFrequency();
      const pointClosestToPeak = helpers.getRelevantPointClosestToPeak(ctx.props.data[0], peakFrequency);

      if (selectedPoint.x === pointClosestToPeak.x) {
        harmonicsMarkerSuffix = 'x';
        harmonicsStartPoint = pointClosestToPeak;
      }
    }

    //guard against too many lines
    if ((diffXcoord < 20 || diffXvalue < 1) && cursorType === 'sideband') {
      d3.selectAll(`.${this.props.chartName}.cursorGroup`).remove();
      this.drawCursor(ctx, { ...referencePoint, text: `${initialCursorText}${harmonicsMarkerSuffix}` }, cursorType);
      return;
    }

    const rightCursors = (ctx.props.data[0][ctx.props.data[0].length - 1].x - referencePoint.x) / diffXvalue;
    const leftCursors = Math.floor(referencePoint.x / diffXvalue);

    d3.selectAll(`.${this.props.chartName}.cursorGroup`).remove();
    this.cursors = [{ ...referencePoint, text: `${initialCursorText}${harmonicsMarkerSuffix}` }];
    const coordStep = Math.ceil(20 / diffXcoord);
    for (let i = 1; i < leftCursors; i++) {
      let closestPoint;
      if (cursorType === 'sideband') {
        const xValue = referencePoint.x - diffXvalue * i;
        closestPoint = helpers.closestXDataPointOnDataset(ctx.props.data[0], xValue);
        closestPoint.text = Math.round(diffXvalue * i) * (-1);
      }
      else if (cursorType === 'harmonic') {
        if (coordStep > 1 && i % coordStep !== 0) continue;
        const xValue = referencePoint.x - diffXvalue * i;
        const point = helpers.findClosestPeak(ctx.props.data, Math.round(leftCursors - i), diffXvalue, shaftSpeed, peakSearchThreshold);
        closestPoint = point || { y: 0, x: xValue };
        closestPoint = { ...closestPoint, text: `${Math.round(leftCursors - i)}${harmonicsMarkerSuffix}` };
      }
      this.cursors.push(closestPoint);
    }
    for (let i = 1; i <= rightCursors; i++) {
      const xValue = (harmonicsStartPoint ? harmonicsStartPoint.x : referencePoint.x) + diffXvalue * i;
      let closestPoint;
      if (cursorType === 'sideband') {
        closestPoint = helpers.closestXDataPointOnDataset(ctx.props.data[0], xValue);
        closestPoint.text = Math.round(diffXvalue * i);
      }
      else if (cursorType === 'harmonic') {
        if (coordStep > 1 && i % coordStep !== 0) continue;
        const point = helpers.findClosestPeak(ctx.props.data, i + harmonicNumber, diffXvalue, shaftSpeed, peakSearchThreshold);
        closestPoint = point || { y: 0, x: xValue };
        closestPoint = {
          ...closestPoint,
          text: (harmonicsMarkerSuffix === 'x' && harmonicNumber > 1)
            ? `${i + harmonicNumber - 1}${harmonicsMarkerSuffix}`
            : `${i + harmonicNumber}${harmonicsMarkerSuffix}`
        };
      }
      this.cursors.push(closestPoint);
    }

    // Cache chart bounds to avoid repeated DOM queries
    const chartBounds = ctx.chart.getBoundingClientRect();
    const chartWidth = chartBounds.width;

    this.cursors.forEach((cursor, index) => {
      const cursorXPos = ctx.x(cursor.x);

      if (cursorXPos < 0 || cursorXPos > chartWidth) {
        return;
      }

      this.drawCursor(ctx, cursor, cursorType);
    });
  }

  drawFeature(ctx, feature, idx = null) {
    const id = idx ? idx : feature.id;
    // dont draw individual features that are off the selected width of chart
    if (!this.ctx.chart) return;
    if (feature.type === 'individual' && (this.ctx.x(feature.frequency) > this.ctx.chart.getBoundingClientRect().width || this.ctx.x(feature.frequency) < 0)) return;

    // TODO: support for area like features (should be done similarly as here just using a rect instead of a line)
    if (!(feature.frequency || (feature.frequency_start && feature.frequency_end))) return;

    d3.select(`#feature-${this.props.chartName}-${id}`).remove();
    // draw single feature line into the container
    const freq_range = feature.type === 'range' ? (feature.frequency_end - feature.frequency_start) : 0;
    const featureG = this.featureContainer
      .append('g')
      .attr('class', `${this.props.chartName} featureGroup`)
      .attr('id', `feature-${this.props.chartName}-${id}`)
      .attr('transform', `translate(${ctx.x(feature.type === 'range' ? feature.frequency_start + (freq_range / 2) : feature.frequency)}, 0)`);

    if (feature.type === 'range') {
      const options = {
        extent: [
          ctx.x(feature.frequency_start),
          ctx.x(feature.frequency_end)
        ]
      };
      d3.select(`.feature-${this.props.chartName}-${id}.brush`).remove();
      this.ctx.brush = null;
      this.ctx.brush = graphUtils.addHorizontalBrush(this.ctx.chart, `feature-${this.props.chartName}-${id} featureBrush`, options);

      const brushEl = d3.select(`.feature-${this.props.chartName}-${id}.brush`);

      brushEl.select('.overlay')
        .attr('pointer-events', 'none');

      brushEl.select('.selection')
        .attr('pointer-events', 'none');

      brushEl.selectAll('.handle')
        .attr('pointer-events', 'none');
    } else {
      // Use custom color for the line if specified
      const lineColor = feature.customColor || '#383838';
      graphUtils.drawLineOnPos(featureG.node(), 0, 0, 0, ctx.chart.getBoundingClientRect().height, `${this.props.chartName} featurePosition`)
        .attr('stroke-width', 3)
        .attr('fill', lineColor);
    }
    // TODO: draw rect on pos and draw text on pos util funcs to be added (also overwrite from places where this would be used otherwise)
    let opacity = 1;
    if (this.props.isFeatureIncluded && !this.props.isFeatureIncluded(feature.frequency)) opacity = 0.5;

    // Use custom color for the rectangle if specified
    const rectColor = feature.customColor || '#BCBFB8';
    const featureRect = featureG.append('rect')
      .attr('height', 16)
      .attr('width', 0)
      .attr('fill', rectColor)
      .attr('id', `feature-${this.props.chartName}-${id}_tooltip_rect`)
      .attr('cursor', 'pointer')
      .attr('opacity', opacity)
      .attr('pointer-events', 'all');

    if (this.props.onFeatureDetailsClick) {
      graphUtils.addMouseClickEvent(featureRect.node(), (e) => {
        e = e || window.event;
        const dataPoint = helpers.closestExactDataPoint(ctx);
        if (!dataPoint) return;
        const dataPos = {
          x: ctx.x(dataPoint.x),
          y: -8
        };
        this.setState({
          dataPos,
          dataToDisplay: feature,
          showFeatureContainer: true,
          showNewPeakModal: false
        });
        if (e) e.stopPropagation();
      });
    }

    const featureText = featureG.append('text')
      .attr('dx', 0)
      .attr('dy', 12)
      .attr('fill', '#fff')
      .attr('font-size', '10px')
      .text(this.props.lowerCaseFeatures ? feature.marker : feature.marker.toUpperCase())
      .attr('pointer-events', 'none');

    const widthOfFeatureLabel = featureText.node().getBBox().width;
    d3.select(`#feature-${this.props.chartName}-${id}_tooltip_rect`)
      .attr('width', widthOfFeatureLabel + 10)
      .attr('transform', `translate(-${(widthOfFeatureLabel + 10) / 2}, -16)`);

    d3.select(`#feature-${this.props.chartName}-${id} text`)
      .attr('transform', `translate(-${(widthOfFeatureLabel) / 2}, -16)`);

    // dont draw range type features label rect if middle of range (where label rect is) is out of bounds of chart
    if (feature.type === 'range') {
      const featureMiddle = feature.frequency_end - (feature.frequency_end - feature.frequency_start) / 2;
      if (this.ctx.x(featureMiddle) > this.ctx.chart.getBoundingClientRect().width || this.ctx.x(featureMiddle) < 0) {
        d3.select(`#feature-${this.props.chartName}-${id}_tooltip_rect`).remove();
        d3.select(`#feature-${this.props.chartName}-${id} text`).remove();
      }
    }
  }

  drawFeatures() {
    if (!this.ctx || !this.props.features || !this.state.features) return;
    const { multipleSpeedMarkers, colors, componentNode, hoveredLegendItem, activeLegendItem, showOverlay, selectedPoints, xUnit } = this.props;

    //  Always clear all existing features first
    d3.selectAll(`.${this.props.chartName}.featureGroup`).remove();

    // Determine which legend item is currently focused (either hovered or active)
    const focusedLegendItem = activeLegendItem !== null ? activeLegendItem : hoveredLegendItem;
    const hasFocusedItem = focusedLegendItem !== null && focusedLegendItem !== undefined;

    //  Check if overlay is active AND has valid spectrum data
    // Use narrowband filtering to ensure consistency with trend chart and legend
    const { getFilteredPointsForDisplay } = require('utils/overlayUtils');
    const filteredPoints = getFilteredPointsForDisplay(selectedPoints, this.props.feature, showOverlay);
    const isOverlayActive = showOverlay && Array.isArray(filteredPoints) && filteredPoints.length > 0;
    const hasValidOverlayData = isOverlayActive && filteredPoints.some(point => point.spectrum_data);

    //  Handle coordinate system consistency during overlay transitions
    const currentXUnit = this.state.xUnit || xUnit || 'Hz';
    const featuresToDraw = this.state.features.items || [];

    const normUnit = u => (u == null || u === '' ? 'Hz' : String(u));
    const alignFeatureFrequency = (feature, sourceU, targetU, shaftForOrders) => {
      const su = normUnit(sourceU);
      const tu = normUnit(targetU);
      if (su.toLowerCase() === tu.toLowerCase()) return feature;
      const ordersInvolved = /orders/i.test(su) || /orders/i.test(tu);
      const spd = shaftForOrders && shaftForOrders > 0.001 ? shaftForOrders : null;
      if (ordersInvolved && !spd) return feature;
      const factor = getFreqConversionFactor(su, tu, spd, 'Hz');
      if (!Number.isFinite(factor) || factor <= 0) return feature;
      return { ...feature, frequency: feature.frequency * factor, freq_units: tu };
    };

    // Draw regular features when NOT in overlay mode OR when overlay is active but no valid data yet
    if (!isOverlayActive || !hasValidOverlayData) {
      //  Ensure features are in the correct coordinate system for current X-axis unit
      if (featuresToDraw.length > 0) {
        const chartShaft = this.getShaftSpeed();
        featuresToDraw.forEach((feature, idx) => {
          const featureUnit = feature.freq_units || 'Hz';
          const adjustedFeature = alignFeatureFrequency(feature, featureUnit, currentXUnit, chartShaft);
          this.drawFeature(this.ctx, adjustedFeature, idx + 1);
        });
      }
    }

    //  Draw overlay markers only when we have valid overlay data
    if (hasValidOverlayData) {
      // Get points with valid spectrum data from the already-filtered list
      const validPoints = filteredPoints.filter(point => point.spectrum_data);

      // For Orders mode overlay, markers should use per-point shaft speed
      // This ensures markers align correctly with their respective spectra
      const SHAFT_SPEED_THRESHOLD = 0.001;
      const overlayShaftSpeed = this.state.referenceShaftSpeed || this.getShaftSpeed();
      let consistentShaftSpeed = overlayShaftSpeed;

      // For Orders mode, use consistent shaft speed resolution from spectrumTransformations
      if (currentXUnit === 'Orders') {
        const { getConsistentShaftSpeed } = require('common/utils/spectrumTransformations');
        consistentShaftSpeed = getConsistentShaftSpeed(validPoints, overlayShaftSpeed);

        // Add validation for development debugging
        if (process.env.NODE_ENV === 'development' && validPoints.length > 1) {
          const pointSpeeds = validPoints
            .map(p => p.shaft_speed || p.speed || p.referenceShaftSpeed)
            .filter(speed => typeof speed === 'number' && speed > SHAFT_SPEED_THRESHOLD);

          const hasInconsistentSpeeds = pointSpeeds.some(speed =>
            Math.abs(speed - consistentShaftSpeed) > SHAFT_SPEED_THRESHOLD
          );
        }
      }

      const transformFeatureCoordinates = (feature, sourceUnit, targetUnit, shaftSpeed) =>
        alignFeatureFrequency(feature, sourceUnit, targetUnit, shaftSpeed);

      // Create mapping from original indexes to current array positions
      const pointIndexMap = {};
      validPoints.forEach((point, idx) => {
        // Primary: Use point's originalIndex if available
        if (point.originalIndex !== undefined) {
          pointIndexMap[point.originalIndex] = idx;
        }
        // Secondary: Use point's current pointIndex if available
        if (point.pointIndex !== undefined) {
          pointIndexMap[point.pointIndex] = idx;
        }
        // Fallback: Use array position
        pointIndexMap[idx] = idx;
      });

      // Draw markers for each selected point
      validPoints.forEach((point, pointIndex) => {
        // Only draw markers for the focused legend item or all markers if nothing is focused
        if (!hasFocusedItem || focusedLegendItem === pointIndex) {
          // Get the color for this point - use assigned color if available, otherwise fall back to colors array
          const color = point.color || (colors && colors[pointIndex % colors.length]) || '#383838';

          //  Check if the point has properly transformed markers
          if (point.spectrumFeatures &&
            point.spectrumFeatures.items &&
            Array.isArray(point.spectrumFeatures.items)) {

            // Draw markers with proper coordinate system handling
            point.spectrumFeatures.items.forEach((marker, markerIdx) => {
              // Ensure markers are associated with the correct points even after filtering
              let markerPointIndex = pointIndex; // Default to current point index

              // If marker has a pointIndex, try to map it to the current filtered array
              if (marker.pointIndex !== undefined) {
                const mappedIndex = pointIndexMap[marker.pointIndex];
                if (mappedIndex !== undefined) {
                  markerPointIndex = mappedIndex;
                }
              }

              // Ensure marker is in the correct coordinate system using helper function
              const markerUnit = marker.freq_units || point.xUnit || 'Hz';

              // Get the shaft speed for this specific point
              const pointShaftSpeed = getPointShaftSpeed(point, overlayShaftSpeed);

              // If marker is already in Orders and we're in Orders mode, no conversion needed
              // Otherwise, transform using the point's own shaft speed
              const adjustedMarker = (markerUnit === currentXUnit && currentXUnit === 'Orders')
                ? marker
                : transformFeatureCoordinates(marker, markerUnit, currentXUnit, pointShaftSpeed || overlayShaftSpeed);

              const finalMarker = {
                ...adjustedMarker,
                customColor: marker.color || color, // Use marker's color if available, otherwise point color
                pointIndex: markerPointIndex, // Use correct point association
                // store the shaft speed of the spectrum this marker belongs to.
                // The feature popup converts Orders->Hz using this value (otherwise it uses a global shaftSpeed).
                pointShaftSpeed: pointShaftSpeed || overlayShaftSpeed
              };

              this.drawFeature(this.ctx, finalMarker, `point_${markerPointIndex}_marker_${markerIdx}`);
            });
          }
          // Fallback: If point doesn't have its own markers, use default features with point's color
          else if (featuresToDraw.length > 0) {
            // Get the shaft speed for this specific point
            const pointShaftSpeed = getPointShaftSpeed(point, overlayShaftSpeed);

            featuresToDraw.forEach((feature, idx) => {
              // Apply coordinate transformation using per-point shaft speed
              const featureUnit = feature.freq_units || 'Hz';
              const adjustedFeature = transformFeatureCoordinates(feature, featureUnit, currentXUnit, pointShaftSpeed || overlayShaftSpeed);

              const coloredFeature = {
                ...adjustedFeature,
                customColor: color,
                // Same rationale as above: ensure popup converts Orders->Hz using this point's speed.
                pointShaftSpeed: pointShaftSpeed || overlayShaftSpeed
              };
              this.drawFeature(this.ctx, coloredFeature, `point_${pointIndex}_marker_${idx}`);
            });
          }
        }
      });
    }
  }

  highlightSelectedFeature() {
    const { dataToDisplay } = this.state;

    // reset previous selection
    d3.selectAll(`.${this.props.chartName}.featureGroup line`)
      .classed('selected', false);
    d3.selectAll(`.${this.props.chartName}.featureGroup rect`)
      .attr('fill', '#BCBFB8');
    d3.selectAll('.featureBrush.brush .selection')
      .attr('fill', '#777');

    if (dataToDisplay) {
      this.drawFeature(this.ctx, dataToDisplay);
      d3.select(`#feature-${this.props.chartName}-${dataToDisplay.id} line`)
        .classed('selected', true);
      d3.select(`#feature-${this.props.chartName}-${dataToDisplay.id} rect`)
        .attr('fill', '#91b846');
      d3.select(`.feature-${this.props.chartName}-${dataToDisplay.id}.brush .selection`)
        .attr('fill', '#91b846');
    }
  }

  closeFeatureContainer() {
    this.setState({
      showFeatureContainer: false,
      dataToDisplay: {}
    });
  }

  findNewXunit(currXunit) {
    // Single source of truth for the Hz -> CPM -> Orders cycle lives in spectrumDisplay.
    // Pass the overlay/tag context so the cycle skips Orders whenever it isn't
    // currently displayable (same rule as the resolver) — otherwise the cycle could
    // propose a unit equal to the current request but not the current display,
    // making the toggle a no-op.
    return nextSpectrumXUnit(currXunit, {
      shaftSpeed: this.props.shaftSpeed,
      tagType: this.props.tagType,
      selectedPoints: this.props.selectedPoints,
      showOverlay: this.props.showOverlay
    });
  }

  findNewYunit(currYunit) {
    if (currYunit === 'dB') return this.props.initialYunit;
    return 'dB';
  }

  redraw(ctx) {
    this.ctx = ctx; // set ctx again (features has wrong context otherwise)
    if (this.cursors) {
      d3.selectAll(`.${this.props.chartName}.cursorGroup`).remove();

      this.cursors.forEach((cursor) => {
        if (ctx.x(cursor.x) > ctx.chart.getBoundingClientRect().width || ctx.x(cursor.x) < 0) return;
        const redrawnCursor = this.getRedrawnCursor(cursor);
        if (!redrawnCursor) return;
        this.drawCursor(ctx, redrawnCursor, this.state.cursorType);
      });
    }
    this.closeFeatureContainer();
    this.drawFeatures();
    if (this.props.spectralEnvelope && !this.props.showOverlay) {
      d3.selectAll(`.${this.props.chartName}.step-graph.circle`).remove();
      this.drawEnvelope();
    }
  }

  // Do not remove this, its being used by LineChart hoc
  storeBrushSelection(selection) {
    this.setState({
      storeBrushSelection: _.cloneDeep(selection)
    });
  }

  toggleYaxisUnits() {
    const { yUnit, points, xUnit } = this.state;
    const {
      toggleSpectrumAmplitudeUnits,
      setPointsToCorrectUnits,
      showOverlay,
      selectedPoints,
      updateSelectedPoints,
      initialXunit,
      initialYunit
    } = this.props;

    const newYunit = this.findNewYunit(yUnit);
    const reference = getAmplitudeConversionReference(yUnit, newYunit);

    // Controlled-units mode: the parent owns the amplitude unit and converts all
    // data (including overlay points) from pristine sources. Only remap the local
    // cursor positions (chart-visual state) and request the change.
    if (this.props.controlledUnits) {
      let remapY = null;
      if (reference) {
        if (newYunit.toLowerCase() === 'db' && yUnit.toLowerCase() !== 'db') {
          remapY = y => convertToDbScale(y, reference);
        } else if (newYunit.toLowerCase() !== 'db' && yUnit.toLowerCase() === 'db') {
          remapY = y => convertFromDbScale(y, reference);
        }
      }

      if (remapY) {
        if (this.cursors) {
          this.cursors.forEach((c) => { c.y = remapY(c.y); });
        }
        const newPoints = _.cloneDeep(points).map(p => ({ ...p, y: remapY(p.y), yUnit: newYunit }));
        if (setPointsToCorrectUnits) setPointsToCorrectUnits(newPoints);
        this.setState({ points: newPoints, yUnit: newYunit }, () => this.updateCursorInfo());
      } else {
        this.setState({ yUnit: newYunit });
      }

      if (toggleSpectrumAmplitudeUnits) toggleSpectrumAmplitudeUnits(newYunit);
      return;
    }

    // Update the Y-axis label immediately
    this.setState({ yUnit: newYunit });

    // Convert data asynchronously
    setTimeout(() => {
      // Get the shaft speed for coordinate transformations
      const shaftSpeed = this.getShaftSpeed();

      // Get spectrum data with updated Y unit
      const spectrumData = this.getDataInCorrectYunit(newYunit);

      // Update cursor Y values
      if (this.cursors) {
        if (newYunit.toLowerCase() === 'db') {
          this.cursors.forEach(c => { c.y = convertToDbScale(c.y, reference) });
        } else if (yUnit.toLowerCase() === 'db') {
          this.cursors.forEach(c => { c.y = convertFromDbScale(c.y, reference) });
        }
      }

      // Update points with new Y unit
      let newPoints = _.cloneDeep(points);
      if (newYunit.toLowerCase() === 'db') {
        newPoints.forEach((p) => {
          p.y = convertToDbScale(p.y, reference)
          p.yUnit = newYunit;
        });
      } else if (yUnit.toLowerCase() === 'db') {
        newPoints.forEach((p) => {
          p.y = convertFromDbScale(p.y, reference)
          p.yUnit = newYunit;
        });
      }
      setPointsToCorrectUnits(newPoints);

      // Handle conversion for overlay mode
      if (showOverlay && selectedPoints && updateSelectedPoints) {
        // Create a deep clone of selectedPoints to avoid reference issues
        const updatedSelectedPoints = _.cloneDeep(selectedPoints);

        // Convert spectrum_data for each selected point and mark with the new unit
        updatedSelectedPoints.forEach((point, index) => {
          if (point.spectrum_data) {
            // Determine original source data and units
            let convertedData;
            const sourceUnit = point.yUnit || yUnit;
            const targetUnit = newYunit;

            // Always use original data for accurate conversions if available
            if (point.originalData && Array.isArray(point.originalData)) {
              // First, get source data
              convertedData = _.cloneDeep(point.originalData);

              // Determine the original amplitude unit
              const originalYUnit = point.originalResponse ? point.originalResponse.amp_units : initialYunit;

              // Determine if we need conversion between Hz and Orders for X-axis
              const originalXUnit = point.originalResponse ? point.originalResponse.freq_units : initialXunit;
              const currentXUnit = point.xUnit || xUnit;
              const needsXConversion = (currentXUnit !== originalXUnit);

              // First convert to target Y unit from original data
              if (targetUnit.toLowerCase() === 'db' && originalYUnit.toLowerCase() !== 'db') {
                const convRef = getAmplitudeConversionReference(originalYUnit, 'db');
                if (convRef) {
                  convertedData = convertedData.map(d => ({
                    x: d.x, // Keep original X coordinates
                    y: convertToDbScale(d.y, convRef)
                  }));
                }
              } else if (targetUnit.toLowerCase() !== 'db' && originalYUnit.toLowerCase() === 'db') {
                const convRef = getAmplitudeConversionReference('db', targetUnit);
                if (convRef) {
                  convertedData = convertedData.map(d => ({
                    x: d.x, // Keep original X coordinates
                    y: convertFromDbScale(d.y, convRef)
                  }));
                }
              }

              // If the current X-axis is in Orders but original data is in Hz (or vice versa),
              // reapply the X-axis transformation
              if (needsXConversion && shaftSpeed > 0) {
                const factor = getFreqConversionFactor(originalXUnit, currentXUnit, shaftSpeed);
                convertedData = convertedData.map(d => ({
                  x: d.x * factor, // Apply X-axis transformation
                  y: d.y // Keep Y values as already converted
                }));
              }
            } else {
              // Fall back to direct conversion if no original data
              convertedData = _.cloneDeep(point.spectrum_data);

              if (targetUnit.toLowerCase() === 'db' && sourceUnit.toLowerCase() !== 'db') {
                // Convert to dB
                convertedData = convertedData.map(d => ({
                  x: d.x,
                  y: convertToDbScale(d.y, reference)
                }));
              } else if (targetUnit.toLowerCase() !== 'db' && sourceUnit.toLowerCase() === 'db') {
                // Convert from dB
                convertedData = convertedData.map(d => ({
                  x: d.x,
                  y: convertFromDbScale(d.y, reference)
                }));
              }
            }

            updatedSelectedPoints[index].spectrum_data = convertedData;
          }

          // Always update the point's yUnit to the current unit
          updatedSelectedPoints[index].yUnit = newYunit;
        });

        // Update selected points in the parent component
        updateSelectedPoints(updatedSelectedPoints);
      }

      this.setState({ points: newPoints }, () => {
        this.updateCursorInfo();
      });

      if (toggleSpectrumAmplitudeUnits) toggleSpectrumAmplitudeUnits(newYunit, spectrumData);
    }, 10);
  }

  updateSpectrumXScale = (xScale) => {
    this.setState({ spectrumXScale: xScale });
    this.props.setSpectrumXScale(xScale);
  }

  toggleXaxisUnits(toggle_unit = null) {
    const {
      data,
      selectedPoints,
      updateSelectedPoints,
      initialXunit,
      toggleSpectrumFrequencyUnits,
      setPointsToCorrectUnits,
      showOverlay,
      shaftSpeedUnit
    } = this.props;

    const {
      xUnit,
      yUnit,
      envelopeXUnit,
      referenceShaftSpeed,
      points,
      spectralEnvelope,
      dataPoint,
      cursorType
    } = this.state;

    if (!data) return;

    // Determine source and target units
    const sourceUnit = xUnit;
    const targetUnit = toggle_unit != null ? toggle_unit : this.findNewXunit(sourceUnit);
    const backendFrequencyUnit = initialXunit || sourceUnit || 'Hz';
    const storedUnitOnPoints = points && points[0] && points[0].spectrumFreqUnit;

    const shaft_speed = referenceShaftSpeed || this.getShaftSpeed();

    const filterTransformedCursors = (arr, spectrumRows) => {
      if (!arr || !Array.isArray(arr)) return arr;
      const firstRow = spectrumRows && spectrumRows.length > 0 && Array.isArray(spectrumRows[0]) ? spectrumRows[0] : null;
      return arr.filter((cursor) => {
        if (!cursor || typeof cursor.x !== 'number' || isNaN(cursor.x) || cursor.x < 0) {
          return false;
        }
        if (firstRow && firstRow.length > 0) {
          const maxX = Math.max(...firstRow.map(d => d.x));
          if (cursor.x > maxX * 2) {
            return false;
          }
        }
        return true;
      });
    };

    /**
     * Apply a conversion factor to state points, dataPoint, and visual cursors (this.cursors).
     * Returns { transformedCursors, transformedDataPoint }.
     */
    const applyFactorToCursors = (factor, fromPoints, fromDataPoint, target, spectrumRows) => {
      if (!Number.isFinite(factor) || factor <= 0) {
        return { transformedCursors: fromPoints, transformedDataPoint: fromDataPoint };
      }

      let transformedCursors = (fromPoints || []).map((p) => this.convertCursorPoint(p, p.spectrumFreqUnit || sourceUnit, target));
      transformedCursors = filterTransformedCursors(transformedCursors, spectrumRows);

      let transformedDataPoint = fromDataPoint;
      if (cursorType === 'sideband' && fromDataPoint && typeof fromDataPoint.x === 'number' && Number.isFinite(fromDataPoint.x)) {
        transformedDataPoint = this.convertCursorPoint(fromDataPoint, fromDataPoint.spectrumFreqUnit || sourceUnit, target);
      }

      if (this.cursors && Array.isArray(this.cursors) && this.cursors.length > 0) {
        this.cursors = this.cursors.map((c) => ({
          ...c,
          x: typeof c.x === 'number' ? c.x * factor : c.x,
          spectrumFreqUnit: target
        }));
      } else {
        this.cursors = transformedCursors;
      }

      return { transformedCursors, transformedDataPoint };
    };

    const commitCursorState = (transformedCursors, transformedDataPoint, target, extraState, callback) => {
      if (setPointsToCorrectUnits) {
        setPointsToCorrectUnits(transformedCursors);
      }
      this.updateSpectrumXScale(target === 'Orders' ? 'orders' : 'frequency');
      this.setState({
        points: transformedCursors,
        dataPoint: transformedDataPoint,
        xUnit: target,
        referenceShaftSpeed: shaft_speed,
        ...extraState
      }, () => {
        this.updateCursorInfo();
        if (this.ctx && this.cursors && this.cursors.length > 0 && this.state.cursorType) {
          this.redrawCursors(this.ctx);
        }
        if (callback) callback();
      });
    };

    // UI unit already matches target, but cursor x values are still in storedUnitOnPoints (e.g. Hz
    // coordinates while the axis label shows CPM). Only remap cursors — spectrum data is already correct.
    if (sourceUnit === targetUnit && storedUnitOnPoints && storedUnitOnPoints !== targetUnit) {
      if (!shaft_speed && (targetUnit === 'Orders' || storedUnitOnPoints === 'Orders')) {
        return;
      }
      const staleFactor = getFreqConversionFactor(storedUnitOnPoints, targetUnit, shaft_speed, backendFrequencyUnit);
      if (!Number.isFinite(staleFactor) || staleFactor <= 0) return;

      const { transformedCursors, transformedDataPoint } = applyFactorToCursors(staleFactor, points, dataPoint, targetUnit, data);
      commitCursorState(transformedCursors, transformedDataPoint, targetUnit, {});
      return;
    }

    if (sourceUnit === targetUnit) return;

    // Controlled-units mode: the parent owns unit state and performs all data
    // conversion from pristine data. Only request the change here; converted data
    // and the new unit then flow back down together as props (cursor remapping is
    // handled by the props.xUnit change handler in componentDidUpdate).
    if (this.props.controlledUnits) {
      if (toggleSpectrumFrequencyUnits) toggleSpectrumFrequencyUnits(targetUnit);
      return;
    }

    if (!shaft_speed && (targetUnit === 'Orders' || sourceUnit === 'Orders')) {
      return;
    }

    // Align cursor reference points to the same frequency unit as spectrum data (sourceUnit)
    let pointsAlignedToSource = this.state.points;
    let dataPointAligned = dataPoint;
    if (storedUnitOnPoints && storedUnitOnPoints !== sourceUnit) {
      const alignFactor = getFreqConversionFactor(storedUnitOnPoints, sourceUnit, shaft_speed, backendFrequencyUnit);
      if (Number.isFinite(alignFactor) && alignFactor > 0) {
        pointsAlignedToSource = (points || []).map((p) => this.convertCursorPoint(p, storedUnitOnPoints, sourceUnit));
        if (cursorType === 'sideband' && dataPoint && typeof dataPoint.x === 'number' && Number.isFinite(dataPoint.x)) {
          dataPointAligned = this.convertCursorPoint(dataPoint, storedUnitOnPoints, sourceUnit);
        }
      }
    }

    const cursorTransformFactor = getFreqConversionFactor(sourceUnit, targetUnit, shaft_speed, backendFrequencyUnit);

    const transformedData = transformSpectrumCoordinates({
      sourceUnit,
      targetUnit,
      shaftSpeed: shaft_speed,
      shaftSpeedUnit: backendFrequencyUnit,
      spectrumData: data,
      originalData: this.state.originalData,
      originalDataUnit: backendFrequencyUnit,
      features: this.state.features,
      originalFeatures: this.props.features,
      originalFeaturesUnit: backendFrequencyUnit,
      selectedPoints: showOverlay ? selectedPoints : null,
      cursors: pointsAlignedToSource
    });

    let transformedEnvelope = spectralEnvelope;
    if (spectralEnvelope && spectralEnvelope.envelope_data) {
      transformedEnvelope = getScaledEnvelope(spectralEnvelope, envelopeXUnit, targetUnit, shaft_speed, shaftSpeedUnit);
    }

    if (showOverlay && selectedPoints && updateSelectedPoints) {
      const updatedPoints = transformedData.selectedPoints.map(point => ({
        ...point,
        yUnit: point.yUnit || yUnit
      }));
      updateSelectedPoints(updatedPoints);
    }

    if (toggleSpectrumFrequencyUnits) {
      toggleSpectrumFrequencyUnits(targetUnit, transformedData.spectrumData);
    }

    // Prefer cursors already transformed by transformSpectrumCoordinates
    let transformedCursors = transformedData.cursors || (points || []).map(p => ({
      ...p,
      xUnit: targetUnit,
      yUnit: p.yUnit || yUnit
    }));
    transformedCursors = (transformedCursors || []).map((p) => ({ ...p, spectrumFreqUnit: targetUnit }));
    transformedCursors = filterTransformedCursors(transformedCursors, transformedData.spectrumData);

    let transformedDataPoint = dataPointAligned;
    if (cursorType === 'sideband' && dataPointAligned && typeof dataPointAligned.x === 'number' &&
        Number.isFinite(dataPointAligned.x) && Number.isFinite(cursorTransformFactor) && cursorTransformFactor > 0) {
      transformedDataPoint = this.convertCursorPoint(dataPointAligned, dataPointAligned.spectrumFreqUnit || sourceUnit, targetUnit);
    }

    if (setPointsToCorrectUnits) {
      setPointsToCorrectUnits(transformedCursors);
    }

    // Transform all visual cursors (harmonic/sideband markers) so markers are preserved across unit changes
    if (this.cursors && Array.isArray(this.cursors) && this.cursors.length > 0 &&
        Number.isFinite(cursorTransformFactor) && cursorTransformFactor > 0) {
      this.cursors = this.cursors.map(c => ({
        ...c,
        x: typeof c.x === 'number' ? c.x * cursorTransformFactor : c.x,
        spectrumFreqUnit: targetUnit
      }));
    } else {
      this.cursors = transformedCursors;
    }

    commitCursorState(transformedCursors, transformedDataPoint, targetUnit, {
      dataToDisplay: transformedData.spectrumData,
      features: transformedData.features,
      spectralEnvelope: transformedEnvelope,
      envelopeXUnit: targetUnit
    }, () => {
      if (transformedEnvelope && !this.props.showOverlay) {
        this.drawEnvelope();
      }
    });
  }

  updateCursorInfo = () => {
    if (!this.state.cursorType) return;

    const referencePoint = this.getReferencePoint();
    if (!referencePoint) return;

    const cursorData = this.state.cursorType === 'sideband' ? this.state.dataPoint : referencePoint;
    this.drawCursorInfotext(this.ctx, cursorData, this.state.cursorType);
  };

  getFeatureTextToDisplay = (feature) => {
    const { xUnit, initialXunit, shaftSpeed, showOverlay, selectedPoints } = this.props;

    // In overlay + Orders mode, each marker needs to be converted back to Hz using the
    // shaft speed of the point it belongs to (not the global/primary shaftSpeed prop).
    let featureShaftSpeed = shaftSpeed;
    if (feature && typeof feature.pointShaftSpeed === 'number' && feature.pointShaftSpeed > 0.001) {
      featureShaftSpeed = feature.pointShaftSpeed;
    } else if (showOverlay && selectedPoints && selectedPoints.length > 0) {
      const idx = (feature && feature.originalPointIndex !== undefined)
        ? feature.originalPointIndex
        : (feature && feature.pointIndex !== undefined ? feature.pointIndex : null);
      if (idx !== null && selectedPoints[idx]) {
        const p = selectedPoints[idx];
        const pSpeed = (p && p.shaft_speed) || (p && p.speed) || (p && p.referenceShaftSpeed);
        if (typeof pSpeed === 'number' && pSpeed > 0.001) {
          featureShaftSpeed = pSpeed;
        }
      }
    }

    if (xUnit === 'Orders') {
      return `${toFixedXIfNumber(feature.frequency * featureShaftSpeed, 2)} ${initialXunit} (${toFixedXIfNumber(feature.frequency, 2)} ${xUnit})`;
    }

    return `${toFixedXIfNumber(feature.frequency, 2)} ${feature.freq_units}`;
  }

  getXDataPoint = () => {
    return _.isArray(this.ctx.props.selection) ? this.ctx.props.selection[0].x : this.ctx.props.selection.x;
  }

  getYDataPoint = () => {
    return _.isArray(this.ctx.props.selection) ? this.ctx.props.selection[0].y : this.ctx.props.selection.y;
  }

  renderComputeContainer = () => {
    const { t } = this.props;
    const { frequencyContribution } = this.state;
    return (
      <ComputeContainer>
        <FrequencyTypeContainer>
          {t('machineCharts.spectrum.frequencyContribution.subSync', { defaultValue: 'Sub-Sync' })}:
        </FrequencyTypeContainer>
        <FreqContributionContainer>{frequencyContribution.subSync}%</FreqContributionContainer>
        <FrequencyTypeContainer>| {t('machineCharts.spectrum.frequencyContribution.sync', { defaultValue: 'Sync' })}:</FrequencyTypeContainer>
        <FreqContributionContainer>{frequencyContribution.sync}%</FreqContributionContainer>
        <FrequencyTypeContainer>| {t('machineCharts.spectrum.frequencyContribution.nonSync', { defaultValue: 'Non-Sync' })}:</FrequencyTypeContainer>
        <FreqContributionContainer>{frequencyContribution.nonSync}%</FreqContributionContainer>
      </ComputeContainer>
    )
  }

  render() {
    const { t } = this.props;
    const feature = this.state.dataToDisplay;
    const { FeatureContainer, editModel, noXlabel, title } = this.props;
    const { frequencyContribution } = this.state;
    const containerWidth = this.ctx && this.ctx.container.getBoundingClientRect().width;
    return (
      <Fragment>
        {!_.isEmpty(frequencyContribution) &&
          !(_.isNaN(frequencyContribution.subSync) || _.isNaN(frequencyContribution.sync) || _.isNaN(frequencyContribution.nonSync))
          && this.renderComputeContainer()}
        {this.state.showNewPeakModal && this.props.measurementTimestamp && (
          <NewPeakModal
            {...this.state.dataPos}
            dataPoint={{
              x: this.getXDataPoint(),
              y: this.getYDataPoint(),
              xUnit: this.ctx.props.xUnit,
              yUnit: this.ctx.props.yUnit
            }}
            width="300"
            alignLeft={this.state.dataPos.x > containerWidth - 400}
            setNewPeak={this.setNewPeak}
            setHarmonicNumber={this.setHarmonicNumber}
            harmonicNumber={this.state.harmonicNumber}
            updateHarmonicFrequencies={this.updateHarmonicFrequencies}
            close={() => this.setState({ showNewPeakModal: false })}
            showSetHarmonicButtons={this.props.showSetHarmonicButtons}
            selectedFrequencyContribution={this.selectedFrequencyContribution()}
            title={title}
          />
        )}
        {!noXlabel && (
          <FrequencyConversionButton
            unit={this.state.xUnit}
            text
            disabled={this.props.disableXaxisToggle}
            onClick={() => this.toggleXaxisUnits()}
            left={containerWidth / 2}
          >
            {this.state.xUnit}
          </FrequencyConversionButton>
        )}
        {(this.state.showFeatureContainer && this.state.dataToDisplay) && (
          <InlineModal
            {...this.state.dataPos}
            height={feature.type === 'Feature' ? "150" : "195"}
            width="320"
            alignLeft={this.state.dataPos.x > containerWidth - 400}
            close={this.closeFeatureContainer}
          >
            {FeatureContainer ? (
              <FeatureContainer
                {...feature}
                editModel={editModel}
                isFeatureIncluded={this.props.isFeatureIncluded}
                changeModelFeatures={this.props.changeModelFeatures}
              />
            ) : (
              <>
                <FlexContainer direction="column">
                  <FlexContainer direction="row">
                    <FeatureInfoContainer>
                      <Label>{t('common.name', { defaultValue: 'Name' })}</Label>
                      <div>
                        <Text>
                          {humanize(feature.name)}
                        </Text>
                      </div>
                    </FeatureInfoContainer>
                  </FlexContainer>
                  <FlexContainer>
                    <FeatureInfoContainer>
                      <Label>{t('common.type', { defaultValue: 'Type' })}</Label>
                      <Text>{feature.type}</Text>
                    </FeatureInfoContainer>
                    <FeatureInfoContainer>
                      <Label>{t('common.frequency', { defaultValue: 'Frequency' })}</Label>
                      {feature.frequency !== null &&
                        <Text>{this.getFeatureTextToDisplay(feature)}</Text>
                      }
                      {!_.isNil(feature.frequency_start) && (
                        <Text>{toFixedXIfNumber(feature.frequency_start)} {feature.freq_units} - {toFixedXIfNumber(feature.frequency_end)} {feature.freq_units}</Text>
                      )
                      }
                    </FeatureInfoContainer>
                  </FlexContainer>
                </FlexContainer>
                <FlexContainer>
                  {feature.type !== 'Feature' && !this.props.analystMode && (
                    <FeatureContainerButton
                      text
                      disabled={false}
                      onClick={() => {
                        const feature = _.cloneDeep(this.state.dataToDisplay);
                        this.props.onFeatureDetailsClick(feature);
                        this.setState({ showFeatureContainer: false, dataToDisplay: null });
                      }}
                    >
                      {t('common.trend', { defaultValue: 'Trend' })}
                    </FeatureContainerButton>
                  )}
                  {feature.feature_type !== 'bearing' && feature.feature_type !== 'forcing_frequencies' && (
                    <FeatureContainerButton
                      text
                      disabled={false}
                      onClick={() => {
                        const feature = _.cloneDeep(this.state.dataToDisplay);
                        this.props.onFeatureEditClick(feature);
                        this.setState({ showFeatureContainer: false, dataToDisplay: null });
                      }}
                    >
                      {t('common.editFeature', { defaultValue: 'Edit feature' })}
                    </FeatureContainerButton>
                  )
                  }
                </FlexContainer>
              </>
            )}
          </InlineModal>
        )}
      </Fragment>
    );
  }
}

const mapStateToProps = state => ({
  currentUser: state.user.user
});

SpectrumChart.displayName = 'SpectrumChart';

SpectrumChart.propTypes = {
  // When true, the parent owns frequency/amplitude unit state and converts data
  // from pristine sources; the chart only *requests* unit changes via the
  // toggleSpectrum*Units callbacks and never transforms spectrum data itself.
  controlledUnits: PropTypes.bool,
  spectrumXScale: PropTypes.string,
  disableXaxisToggle: PropTypes.bool,
  showSetHarmonicButtons: PropTypes.bool,
  spectralEnvelope: PropTypes.object,
  useSpectrumShaftSpeed: PropTypes.bool,
  setSpectrumXScale: PropTypes.func,
  showOverlay: PropTypes.bool,
  selectedPoints: PropTypes.array,
  updateSelectedPoints: PropTypes.func,
  multipleSpeedMarkers: PropTypes.array,
  colors: PropTypes.array,
  componentNode: PropTypes.object,
  shaftSpeed: PropTypes.number,
  hoveredLegendItem: PropTypes.number,
  activeLegendItem: PropTypes.number
};

SpectrumChart.defaultProps = {
  controlledUnits: false,
  spectrumXScale: null,
  disableXaxisToggle: false,
  showSetHarmonicButtons: true,
  spectralEnvelope: null,
  useSpectrumShaftSpeed: false,
  setSpectrumXScale: () => { },
  showOverlay: false,
  selectedPoints: [],
  updateSelectedPoints: () => { },
  multipleSpeedMarkers: [],
  colors: ['#91b846'],
  componentNode: null,
  shaftSpeed: 0,
  hoveredLegendItem: null,
  activeLegendItem: null
};

export default connect(mapStateToProps, {})(withTranslation()(LineChart(SpectrumChart)));
